import { useState, useEffect, useRef } from 'react'
import { X, Plus, Mail, Trash2, Clock, Database, RefreshCw, Lock } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { useSubscription } from '../../contexts/SubscriptionContext'
import {
  getConnectedAccounts,
  removeConnectedAccount,
  ConnectedAccount,
  getUserSettings,
  updateUserSettings,
  UserSettings
} from '../../services/userService'
import AddAccountModal from './AddAccountModal'
import { ProBadge } from '../UI/ProBadge'

interface SettingsPanelProps {
  onClose: () => void
}

const DEFAULT_SETTINGS: UserSettings = {
  theme: 'system',
  notificationsEnabled: true,
  emailRetentionDays: 30,
  autoFetchEnabled: true,
  autoFetchIntervalMinutes: 5
}

export default function SettingsPanel({ onClose }: SettingsPanelProps) {
  const { user } = useAuth()
  const { isPro, canAutoRefresh, allowedRetentionDays, maxAccounts } = useSubscription()
  const [accounts, setAccounts] = useState<ConnectedAccount[]>([])
  const [settings, setSettings] = useState<UserSettings>(DEFAULT_SETTINGS)
  const [loading, setLoading] = useState(true)
  const [savingSettings, setSavingSettings] = useState(false)
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [removingId, setRemovingId] = useState<string | null>(null)
  const [isAnimating, setIsAnimating] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)

  const canAddMoreAccounts = accounts.length < maxAccounts

  // Handle slide-in animation on mount
  useEffect(() => {
    // Trigger animation after mount
    requestAnimationFrame(() => {
      setIsAnimating(true)
    })
  }, [])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Don't close if the add account modal is open
      if (isAddModalOpen) return

      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        onClose()
      }
    }

    // Only add click outside listener for desktop
    const mediaQuery = window.matchMedia('(min-width: 1024px)')
    if (mediaQuery.matches) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [onClose, isAddModalOpen])

  // Prevent body scroll on mobile when panel is open
  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 1023px)')
    if (mediaQuery.matches) {
      document.body.style.overflow = 'hidden'
      return () => {
        document.body.style.overflow = ''
      }
    }
  }, [])

  useEffect(() => {
    loadData()
  }, [user])

  const loadData = async () => {
    if (!user) return
    setLoading(true)
    try {
      const [fetchedAccounts, fetchedSettings] = await Promise.all([
        getConnectedAccounts(user.uid),
        getUserSettings(user.uid)
      ])
      setAccounts(fetchedAccounts)
      if (fetchedSettings) {
        setSettings({ ...DEFAULT_SETTINGS, ...fetchedSettings })
      }
    } catch (error) {
      console.error('Error loading settings:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleRemoveAccount = async (accountId: string) => {
    if (!user || !accountId) return
    setRemovingId(accountId)
    try {
      await removeConnectedAccount(user.uid, accountId)
      setAccounts(accounts.filter((a) => a.id !== accountId))
    } catch (error) {
      console.error('Error removing account:', error)
    } finally {
      setRemovingId(null)
    }
  }

  const handleAccountAdded = () => {
    loadData()
    setIsAddModalOpen(false)
  }

  const handleSettingChange = async (key: keyof UserSettings, value: unknown) => {
    if (!user) return

    const newSettings = { ...settings, [key]: value }
    setSettings(newSettings)

    setSavingSettings(true)
    try {
      await updateUserSettings(user.uid, { [key]: value })
    } catch (error) {
      console.error('Error saving settings:', error)
      // Revert on error
      setSettings(settings)
    } finally {
      setSavingSettings(false)
    }
  }

  const getProviderIcon = (provider: string) => {
    switch (provider) {
      case 'gmail':
        return (
          <svg className="w-5 h-5 lg:w-5 lg:h-5" viewBox="0 0 24 24">
            <path fill="#EA4335" d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"/>
          </svg>
        )
      case 'outlook':
      case 'hotmail':
        return (
          <svg className="w-5 h-5 lg:w-5 lg:h-5" viewBox="0 0 24 24">
            <path fill="#0078D4" d="M24 7.387v10.478c0 .23-.08.424-.238.576-.158.154-.352.23-.58.23h-8.547v-6.959l1.6 1.229c.102.078.224.117.365.117.142 0 .264-.04.365-.117l6.797-5.222c.078-.063.14-.14.186-.23.047-.09.052-.18.052-.27V7.1c0-.193-.1-.29-.297-.29h-.107c-.063.018-.128.05-.195.098l-6.801 5.218c-.102.078-.223.117-.365.117-.142 0-.263-.04-.365-.117l-6.8-5.218c-.068-.049-.133-.08-.196-.098h-.108c-.197 0-.296.097-.296.29v.12c0 .09.018.18.052.27.047.09.108.167.187.23l6.796 5.222c.102.078.224.117.365.117.142 0 .264-.04.365-.117l1.6-1.23v6.96H.82c-.228 0-.42-.077-.58-.23-.157-.152-.238-.347-.238-.576V7.387c0-.326.15-.593.45-.8L11.56 0c.18-.102.333-.102.455 0l11.534 6.587c.3.207.45.474.45.8z"/>
          </svg>
        )
      case 'imap':
        return <Mail className="w-5 h-5 lg:w-5 lg:h-5 text-gray-600" />
      default:
        return <Mail className="w-5 h-5 lg:w-5 lg:h-5 text-gray-600" />
    }
  }

  const getProviderName = (provider: string) => {
    switch (provider) {
      case 'gmail':
        return 'Gmail'
      case 'outlook':
        return 'Office 365'
      case 'hotmail':
        return 'Hotmail'
      case 'imap':
        return 'IMAP'
      default:
        return provider
    }
  }

  return (
    <>
      {/* Mobile backdrop overlay */}
      <div
        className={`fixed inset-0 bg-black transition-opacity duration-300 lg:hidden z-40 ${
          isAnimating ? 'opacity-50' : 'opacity-0'
        }`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Settings Panel - Mobile: full-screen drawer, Desktop: dropdown */}
      <div
        ref={panelRef}
        className={`
          fixed inset-y-0 right-0 w-full sm:w-96 bg-white shadow-xl z-50
          transform transition-transform duration-300 ease-out
          lg:absolute lg:inset-auto lg:right-0 lg:top-full lg:mt-2
          lg:rounded-xl lg:border lg:border-gray-200 lg:max-h-[80vh] lg:transform-none
          ${isAnimating ? 'translate-x-0' : 'translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Header - Enhanced for mobile with larger touch targets */}
        <div className="flex items-center justify-between p-4 lg:p-4 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h3 className="font-semibold text-gray-900 text-lg lg:text-base">Settings</h3>
          <div className="flex items-center gap-3 lg:gap-2">
            {savingSettings && (
              <span className="text-sm lg:text-xs text-gray-400">Saving...</span>
            )}
            <button
              onClick={onClose}
              className="p-2 lg:p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg lg:rounded active:bg-gray-200 touch-manipulation"
              aria-label="Close settings"
            >
              <X size={24} className="lg:hidden" />
              <X size={18} className="hidden lg:block" />
            </button>
          </div>
        </div>

        {/* Scrollable content area */}
        <div className="overflow-y-auto h-[calc(100vh-73px)] lg:h-auto lg:max-h-[calc(80vh-60px)]">
          {/* Email Accounts Section */}
          <div className="p-4 lg:p-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-4 lg:mb-3">
              <div className="flex items-center gap-2">
                <h4 className="text-base lg:text-sm font-medium text-gray-700">Email Accounts</h4>
                <span className="text-xs text-gray-400">({accounts.length}/{maxAccounts})</span>
              </div>
              {canAddMoreAccounts ? (
                <button
                  onClick={() => setIsAddModalOpen(true)}
                  className="flex items-center gap-2 lg:gap-1 text-base lg:text-sm text-primary-600 hover:text-primary-700 font-medium py-2 px-3 lg:py-0 lg:px-0 rounded-lg lg:rounded-none hover:bg-primary-50 lg:hover:bg-transparent active:bg-primary-100 lg:active:bg-transparent touch-manipulation"
                >
                  <Plus size={20} className="lg:hidden" />
                  <Plus size={16} className="hidden lg:block" />
                  Add
                </button>
              ) : (
                <div className="flex items-center gap-1 text-sm text-gray-400">
                  <Lock size={14} />
                  <span className="hidden sm:inline">Upgrade for more</span>
                </div>
              )}
            </div>

            {/* Accounts List */}
            <div className="space-y-3 lg:space-y-2">
              {loading ? (
                <div className="flex items-center justify-center py-6 lg:py-4">
                  <div className="w-6 h-6 lg:w-5 lg:h-5 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : accounts.length === 0 ? (
                <div className="text-center py-8 lg:py-6">
                  <Mail className="w-12 h-12 lg:w-10 lg:h-10 text-gray-300 mx-auto mb-3 lg:mb-2" />
                  <p className="text-base lg:text-sm text-gray-500">No email accounts connected</p>
                  <button
                    onClick={() => setIsAddModalOpen(true)}
                    className="mt-3 lg:mt-2 text-base lg:text-sm text-primary-600 hover:text-primary-700 font-medium py-2 px-4 lg:py-0 lg:px-0 rounded-lg lg:rounded-none hover:bg-primary-50 lg:hover:bg-transparent active:bg-primary-100 touch-manipulation"
                  >
                    Add your first account
                  </button>
                </div>
              ) : (
                accounts.map((account) => (
                  <div
                    key={account.id}
                    className="flex items-center gap-3 p-4 lg:p-3 bg-gray-50 rounded-xl lg:rounded-lg group"
                  >
                    <div
                      className="w-3 h-3 lg:w-2 lg:h-2 rounded-full flex-shrink-0"
                      style={{ backgroundColor: account.color }}
                    />
                    <div className="flex-shrink-0">
                      {getProviderIcon(account.provider)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-base lg:text-sm font-medium text-gray-900 truncate">
                        {account.email}
                      </p>
                      <p className="text-sm lg:text-xs text-gray-500">
                        {getProviderName(account.provider)}
                      </p>
                    </div>
                    <button
                      onClick={() => account.id && handleRemoveAccount(account.id)}
                      disabled={removingId === account.id}
                      className="p-3 lg:p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg lg:rounded opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-all disabled:opacity-50 active:bg-red-100 touch-manipulation"
                      title="Remove account"
                      aria-label="Remove account"
                    >
                      {removingId === account.id ? (
                        <div className="w-5 h-5 lg:w-4 lg:h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>
                          <Trash2 size={20} className="lg:hidden" />
                          <Trash2 size={16} className="hidden lg:block" />
                        </>
                      )}
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Auto-Fetch Settings */}
          <div className="p-4 lg:p-4 border-b border-gray-200">
            <div className="flex items-center gap-2 mb-4 lg:mb-3">
              <RefreshCw size={18} className="lg:hidden text-gray-500" />
              <RefreshCw size={16} className="hidden lg:block text-gray-500" />
              <h4 className="text-base lg:text-sm font-medium text-gray-700">Auto-Fetch</h4>
              {!canAutoRefresh && <ProBadge />}
            </div>

            {canAutoRefresh ? (
              <div className="space-y-4 lg:space-y-3">
                {/* Enable/Disable Toggle */}
                <div className="flex items-center justify-between gap-4">
                  <span className="text-base lg:text-sm text-gray-600">Automatically check for new emails</span>
                  <button
                    onClick={() => handleSettingChange('autoFetchEnabled', !settings.autoFetchEnabled)}
                    className={`relative w-14 h-8 lg:w-11 lg:h-6 rounded-full transition-colors flex-shrink-0 touch-manipulation ${
                      settings.autoFetchEnabled ? 'bg-primary-600' : 'bg-gray-300'
                    }`}
                    role="switch"
                    aria-checked={settings.autoFetchEnabled}
                  >
                    <span
                      className={`absolute top-1 lg:top-0.5 left-1 lg:left-0.5 w-6 h-6 lg:w-5 lg:h-5 bg-white rounded-full shadow transition-transform ${
                        settings.autoFetchEnabled ? 'translate-x-6 lg:translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>

                {/* Interval Selection */}
                {settings.autoFetchEnabled && (
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-base lg:text-sm text-gray-600">Check every</span>
                    <select
                      value={settings.autoFetchIntervalMinutes}
                      onChange={(e) => handleSettingChange('autoFetchIntervalMinutes', Number(e.target.value) as 3 | 5 | 15)}
                      className="text-base lg:text-sm border border-gray-300 rounded-xl lg:rounded-lg px-4 py-3 lg:px-3 lg:py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 touch-manipulation min-h-[44px] lg:min-h-0"
                    >
                      <option value={3}>3 minutes</option>
                      <option value={5}>5 minutes</option>
                      <option value={15}>15 minutes</option>
                    </select>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <Lock size={24} className="text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600 mb-2">Auto-fetch is a Pro feature</p>
                <p className="text-xs text-gray-400">Upgrade to automatically sync your emails</p>
              </div>
            )}
          </div>

          {/* Email Storage Settings */}
          <div className="p-4 lg:p-4 border-b border-gray-200">
            <div className="flex items-center gap-2 mb-4 lg:mb-3">
              <Database size={18} className="lg:hidden text-gray-500" />
              <Database size={16} className="hidden lg:block text-gray-500" />
              <h4 className="text-base lg:text-sm font-medium text-gray-700">Email Storage</h4>
            </div>

            <div className="space-y-4 lg:space-y-3">
              <div className="flex items-center justify-between gap-4">
                <span className="text-base lg:text-sm text-gray-600">Keep emails for</span>
                <div className="flex items-center gap-2">
                  <select
                    value={settings.emailRetentionDays}
                    onChange={(e) => handleSettingChange('emailRetentionDays', Number(e.target.value) as 7 | 30)}
                    className="text-base lg:text-sm border border-gray-300 rounded-xl lg:rounded-lg px-4 py-3 lg:px-3 lg:py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 touch-manipulation min-h-[44px] lg:min-h-0"
                  >
                    <option value={7}>7 days</option>
                    {allowedRetentionDays.includes(30) ? (
                      <option value={30}>30 days</option>
                    ) : (
                      <option value={30} disabled>30 days (Pro)</option>
                    )}
                  </select>
                  {!allowedRetentionDays.includes(30) && settings.emailRetentionDays === 7 && (
                    <Lock size={14} className="text-gray-400" />
                  )}
                </div>
              </div>

              <p className="text-sm lg:text-xs text-gray-400">
                Older emails will be automatically removed to save storage.
                {!isPro && ' Upgrade to Pro for 30-day retention.'}
              </p>
            </div>
          </div>

          {/* Sync Info */}
          <div className="p-4 lg:p-4 border-b border-gray-200">
            <div className="flex items-center gap-2 mb-3 lg:mb-2">
              <Clock size={18} className="lg:hidden text-gray-500" />
              <Clock size={16} className="hidden lg:block text-gray-500" />
              <h4 className="text-base lg:text-sm font-medium text-gray-700">Sync Status</h4>
            </div>
            <p className="text-sm lg:text-xs text-gray-500">
              {settings.autoFetchEnabled
                ? `Emails are automatically synced every ${settings.autoFetchIntervalMinutes} minutes.`
                : 'Auto-fetch is disabled. Click refresh to manually sync.'}
            </p>
          </div>

          {/* Footer */}
          <div className="p-4 lg:p-4 bg-gray-50">
            <p className="text-sm lg:text-xs text-gray-500 text-center">
              InboxOne v0.1.0
            </p>
          </div>

          {/* Extra padding at bottom for mobile to ensure last items are accessible */}
          <div className="h-8 lg:h-0 bg-gray-50" />
        </div>
      </div>

      {/* Add Account Modal */}
      {isAddModalOpen && (
        <AddAccountModal
          onClose={() => setIsAddModalOpen(false)}
          onAccountAdded={handleAccountAdded}
        />
      )}
    </>
  )
}
